/*
  GAROE Moments, Season Stats Board live preview.

  stats-board  Photos show first. Choosing Personalised, or typing a name,
               switches to the live preview. Draws the player's name and season onto the board image as
               the customer types. Standard shows the board blank, the way
               it arrives. Long names shrink to fit, then drop onto two
               lines, so a full name always fits the space under the title.
               Also switches between the preview and the product photos.

  Reads the same inputs the form submits, so the preview can never show
  something different from what reaches the order.
*/
(() => {
  if (customElements.get('stats-board')) return;

  const SVG_NS = 'http://www.w3.org/2000/svg';
  const config = window.GAROE || { strings: {} };
  const strings = config.strings || {};

  class StatsBoard extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-product-section]');
      this.form = this.section ? this.section.querySelector('form') : null;
      this.nameText = this.querySelector('[data-board-name]');
      this.seasonText = this.querySelector('[data-board-season]');
      this.caption = this.querySelector('[data-board-caption]');
      this.desc = this.querySelector('[data-board-desc]');
      this.nameInput = this.form ? this.form.querySelector('[data-board-input="name"]') : null;
      this.seasonInput = this.form ? this.form.querySelector('[data-board-input="season"]') : null;

      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-board-tab]');
        if (tab) this.show(tab.dataset.boardTab);
      });

      if (this.form) {
        this.form.addEventListener('input', () => this.render(true));
        this.form.addEventListener('change', (event) => {
          // Choosing Personalised brings the live preview forward.
          const tier = event.target.closest('input[name="id"]');
          this.render(Boolean(tier && tier.dataset.reveals === 'true'));
        });
      }

      this.render(false);
      // Measuring depends on the board font, so fit again once it has loaded.
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.render(false));
    }

    show(view) {
      this.dataset.view = view;
      this.querySelectorAll('[data-board-tab]').forEach((tab) => {
        tab.setAttribute('aria-pressed', String(tab.dataset.boardTab === view));
      });
      this.querySelectorAll('[data-board-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.boardPanel !== view;
      });
      // Text can only be measured once the preview is visible, so fit it again.
      if (view === 'preview') this.render(false);
    }

    personalised() {
      if (!this.form) return false;
      const checked = this.form.querySelector('input[name="id"]:checked');
      return Boolean(checked && checked.dataset.reveals === 'true');
    }

    render(fromTyping) {
      const personalised = this.personalised();
      const typedName = this.nameInput ? this.nameInput.value.trim() : '';
      const season = this.seasonInput ? this.seasonInput.value.trim() : '';

      let name = '';
      let placeholder = false;
      if (personalised) {
        name = typedName || strings.boardNamePlaceholder || 'YOUR NAME';
        placeholder = !typedName;
      }

      this.setName(name.toUpperCase(), placeholder);
      if (this.seasonText) this.seasonText.textContent = personalised ? season : '';

      if (this.caption) {
        this.caption.textContent = personalised
          ? strings.boardCaptionPersonalised || ''
          : strings.boardCaptionStandard || '';
      }
      if (this.desc) {
        this.desc.textContent = personalised && typedName
          ? `${strings.boardPreviewAlt || ''} ${typedName}, ${season}`.trim()
          : strings.boardPreviewAlt || '';
      }

      // Personalising brings the preview forward if the photos are showing.
      if (fromTyping && personalised && this.dataset.view !== 'preview') this.show('preview');
    }

    /* One line if it fits at a readable size, otherwise two balanced lines. */
    setName(text, placeholder) {
      const el = this.nameText;
      if (!el) return;
      const x = Number(el.dataset.x);
      const y = Number(el.dataset.y);
      const maxWidth = Number(el.dataset.maxWidth);
      const maxSize = Number(el.dataset.maxSize);
      const minSize = Number(el.dataset.minSize);

      el.classList.toggle('is-placeholder', placeholder);
      el.textContent = '';
      if (!text) return;

      el.setAttribute('font-size', maxSize);
      el.textContent = text;
      const width = this.measure(el);
      if (width <= maxWidth) return;

      const oneLineSize = (maxSize * maxWidth) / width;
      const words = text.split(/\s+/);
      if (oneLineSize >= minSize || words.length < 2) {
        el.setAttribute('font-size', oneLineSize.toFixed(1));
        return;
      }

      const [first, second] = this.balance(words);
      el.textContent = '';
      const lines = [first, second].map((line) => {
        const tspan = document.createElementNS(SVG_NS, 'tspan');
        tspan.setAttribute('x', x);
        tspan.textContent = line;
        el.appendChild(tspan);
        return tspan;
      });
      const widest = Math.max(...lines.map((tspan) => this.measure(tspan)));
      const size = Math.min(maxSize * 0.86, (maxSize * maxWidth) / widest);
      el.setAttribute('font-size', size.toFixed(1));
      const gap = size * 0.58;
      lines[0].setAttribute('y', (y - gap).toFixed(1));
      lines[1].setAttribute('y', (y + gap).toFixed(1));
    }

    measure(node) {
      try {
        return node.getComputedTextLength();
      } catch (error) {
        return 0;
      }
    }

    /* Split at the space that makes the two lines closest in length. */
    balance(words) {
      let best = 1;
      let bestDiff = Infinity;
      for (let i = 1; i < words.length; i += 1) {
        const diff = Math.abs(words.slice(0, i).join(' ').length - words.slice(i).join(' ').length);
        if (diff < bestDiff) {
          bestDiff = diff;
          best = i;
        }
      }
      return [words.slice(0, best).join(' '), words.slice(best).join(' ')];
    }
  }

  customElements.define('stats-board', StatsBoard);
})();
